/**
 * Shell del chat con IA del admin: panel lateral (Sheet) con encabezado (historial y nueva
 * conversación) y el layout historial | chat. Lo usan la Guía del portal, el Analista de tickets
 * y el Agente CEO; cada uno pone su historial (`ChatIaHistorial`) y su conversación como hijos.
 *
 * Responsive: debajo de `sm` el panel ocupa toda la pantalla; debajo de `lg` el historial es una
 * vista que tapa el chat; desde `lg` es una columna a la izquierda y el panel se ensancha.
 * El estado (abierto, historial visible) vive en `useChatIaPanel`.
 */
import type { ComponentType, ReactNode } from "react";
import { History, Sparkles, SquarePen } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { IconTip } from "@/components/ui/icon-tip";
import { cn } from "@/lib/utils";

export interface ChatIaPanelProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  titulo: string;
  descripcion: string;
  icono?: ComponentType<{ className?: string }>;
  /** Columna/vista de historial. Sin ella no hay botón de historial. */
  historial?: ReactNode;
  historialAbierto: boolean;
  onAlternarHistorial: () => void;
  onNueva: () => void;
  nuevaDeshabilitada?: boolean;
  /** Botones extra del encabezado (p. ej. abrir en pantalla completa). */
  accionesEncabezado?: ReactNode;
  /** Franja bajo el encabezado (p. ej. el contexto de la pantalla del CEO). */
  subencabezado?: ReactNode;
  /** Columna del chat; normalmente un `ChatIaCuerpo`. */
  children: ReactNode;
}

export function ChatIaPanel({
  abierto,
  onAbiertoChange,
  titulo,
  descripcion,
  icono: Icono = Sparkles,
  historial,
  historialAbierto,
  onAlternarHistorial,
  onNueva,
  nuevaDeshabilitada,
  accionesEncabezado,
  subencabezado,
  children,
}: ChatIaPanelProps) {
  const conHistorial = !!historial && historialAbierto;
  return (
    <Sheet open={abierto} onOpenChange={onAbiertoChange}>
      <SheetContent
        side="right"
        className={cn(
          "flex w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:w-[520px] sm:max-w-[520px]",
          conHistorial ? "lg:w-[880px] lg:max-w-[880px]" : "lg:w-[620px] lg:max-w-[620px]",
        )}
      >
        <SheetHeader className="flex-row items-center gap-3 space-y-0 border-b border-border px-4 py-3 pr-12 text-left">
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
            <Icono className="size-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-[15px] leading-tight">{titulo}</SheetTitle>
            <SheetDescription className="line-clamp-1 text-[12px] leading-snug">{descripcion}</SheetDescription>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {historial && (
              <IconTip label={historialAbierto ? "Ocultar historial" : "Historial"}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={historialAbierto ? "Ocultar historial" : "Ver historial"}
                  aria-pressed={historialAbierto}
                  onClick={onAlternarHistorial}
                  className={cn(
                    "size-8 text-muted-foreground",
                    historialAbierto && "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary",
                  )}
                >
                  <History />
                </Button>
              </IconTip>
            )}
            <IconTip label="Nueva conversación">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Nueva conversación"
                disabled={nuevaDeshabilitada}
                onClick={onNueva}
                className="size-8 text-muted-foreground"
              >
                <SquarePen />
              </Button>
            </IconTip>
            {accionesEncabezado}
          </div>
        </SheetHeader>

        {subencabezado}

        <div className="relative flex min-h-0 flex-1">
          {conHistorial && (
            // Debajo de lg reemplaza al chat; desde lg es una columna a la izquierda.
            <div className="absolute inset-0 z-10 flex bg-background lg:static lg:z-auto lg:w-[264px] lg:shrink-0 lg:border-r lg:border-border">
              {historial}
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col">{children}</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Columna del chat: zona de mensajes con scroll y el composer fijo abajo. Sirve dentro del panel
 * y en una página completa (Agente CEO).
 */
export function ChatIaCuerpo({
  children,
  composer,
  className,
}: {
  children: ReactNode;
  composer?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-h-0 min-w-0 flex-1 flex-col", className)}>
      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5">{children}</div>
      {composer}
    </div>
  );
}
